import { useState, useEffect } from "react";
import { Tab } from "@headlessui/react";
import {
  XMarkIcon,
  ArrowUpTrayIcon,
  FolderIcon,
} from "@heroicons/react/24/outline";
import { assetsApi } from "../../repositories/assets";
import Swal from "sweetalert2";
import { boolean } from "yup";
import React from "react";
import Button from "../ui/Button";
import { Skeleton } from "../ui/skeleton";

interface UploadedFile {
  file: File | any;
  previewUrl: string;
  name: string;
  category?: string;
}

interface SearchParams {
  searchType: "title" | "category";
  searchTerm: string;
}

const IMAGE_CATEGORIES = [
  'Select Category',
  'Press Photos',
  'Cover Art',
  'Single Art',
  'BTS',
  'Other'
];

interface Asset {
  id: number;
  userId: number;
  artistId: string;
  url: string;
  imageTitle: string;
  category: string;
  createdAt: string;
  updatedAt: string;
}

interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  artistId?: string;
  onUploadSuccess?: (assets: Asset[]) => void;
  onSelectionChange: (urls: { url: string; imageTitle: string }[]) => void;
  maxTotalImages?: number;
  showCheckboxes: boolean; // Default to false (no checkboxes)
  initialSelectedImages?: { url: string; imageTitle: string }[]; // Pre-selected images
  defaultTab?: number; // 0 for Upload New Files, 1 for All Assets
}

const MAX_FILES = 5;
const SUPPORTED_FORMATS = ["image/jpeg", "image/jpg", "image/png", "image/gif"];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export default function UploadModal({
  isOpen,
  onClose,
  artistId,
  onUploadSuccess,
  onSelectionChange,
  maxTotalImages,
  showCheckboxes = false,
  initialSelectedImages = [],
  defaultTab = 0,
}: UploadModalProps) {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [previewModal, setPreviewModal] = useState<{
    isOpen: boolean;
    file: UploadedFile | null;
  }>({
    isOpen: false,
    file: null,
  });
  const [existingAssets, setExistingAssets] = useState<Asset[]>([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState<
    { url: string; imageTitle: string }[]
  >([]);
  const [isDragging, setIsDragging] = useState(false);
  // Add these state variables
  const [searchTerm, setSearchTerm] = useState("");
  const [searchType, setSearchType] = useState<"title" | "category">("title");

  useEffect(() => {
    if (isOpen && artistId) {
      loadExistingAssets();
    }
  }, [isOpen, artistId]);

  // Initialize selected assets with pre-selected images
  useEffect(() => {
    if (isOpen && initialSelectedImages.length > 0) {
      setSelectedAssets(initialSelectedImages);
    }
  }, [isOpen, initialSelectedImages]);

  // Clear states when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFiles([]);
      setError(null);
      setExistingAssets([]);
      setSelectedAssets([]);
    }
  }, [isOpen]);

  // Update the loadExistingAssets function
  const loadExistingAssets = async (searchParams?: { title?: string; category?: string }) => {
    if (!artistId) return;
    try {
      setLoadingAssets(true);
      setError(null);

      // Use the updated API call with search parameters
      const response = await assetsApi.getByArtistId(artistId, searchParams);
      setExistingAssets(response.data?.data?.images || []);
    } catch (error: any) {
      console.error("Failed to load assets:", error);
      setError("Failed to load existing assets");
      setExistingAssets([]);
    } finally {
      setLoadingAssets(false);
    }
  };

  // Add search handlers
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSearchType(e.target.value as "title" | "category");
  };

  const clearSearch = () => {
    setSearchTerm("");
    loadExistingAssets();
  };

  // Add the debounced search effect - UNCOMMENT and update this
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.trim()) {
        const searchParams: { title?: string; category?: string } = {};

        if (searchType === 'title') {
          searchParams.title = searchTerm;
        } else if (searchType === 'category') {
          searchParams.category = searchTerm;
        }

        loadExistingAssets(searchParams);
      } else {
        loadExistingAssets(); // Load all assets when search is empty
      }
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, searchType]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles) return;
    setError(null);

    if (
      maxTotalImages &&
      files.length + selectedAssets.length >= maxTotalImages
    ) {
      setError(`Maximum ${maxTotalImages} images allowed.`);
      return;
    }
    const updatedFiles: UploadedFile[] = [];
    const errors: string[] = [];

    Array.from(selectedFiles).forEach((file) => {
      if (!SUPPORTED_FORMATS.includes(file.type)) {
        errors.push(`${file.name}: Only JPG, GIF files are allowed`);
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        errors.push(`${file.name}: File size must be less than 20MB`);
        return;
      }

      const previewUrl = URL.createObjectURL(file);
      updatedFiles.push({ file, previewUrl, name: file.name.split(".")[0] });
    });

    if (errors.length > 0) {
      setError(errors.join("; "));
      return;
    }

    setFiles((prev) => [...prev, ...updatedFiles]);
    e.target.value = "";
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const removeFile = (index: number) => {
    const updatedFiles = files.filter((_, idx) => idx !== index);
    setFiles(updatedFiles);
    setError(null);
  };

  const closePreview = () => {
    setPreviewModal({ isOpen: false, file: null });
  };

  const openAssetPreview = (asset: Asset) => {
    setPreviewModal({
      isOpen: true,
      file: { previewUrl: asset.url, name: asset.imageTitle, file: "" },
    });
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      setError("Please select at least one file to upload");
      return;
    }

    const filesWithoutNames = files.filter((file) => !file.name.trim());
    if (filesWithoutNames.length > 0) {
      setError("Please provide titles for all files");
      return;
    }

    const filesWithoutCategories = files.filter((file) => !file.category || !file.category.trim());
    if (filesWithoutCategories.length > 0) {
      setError("Please select categories for all files");
      return;
    }


    try {
      setUploading(true);
      setError(null);

      const formData = new FormData();
      files.forEach((fileData) => {
        formData.append("images", fileData.file);
      });

      files.forEach((fileData) => {
        formData.append("imageTitles[]", fileData.name.trim());
      });

      files.forEach((fileData) => {
        formData.append("categories[]", fileData.category || 'Select Category');
      });

      const response = artistId
        ? await assetsApi.create(artistId, formData)
        : await assetsApi.createWithoutArtist(formData);
      if (response.data.success) {
        const uploadedAssets = response.data.data.images;

        if (onUploadSuccess) {
          onUploadSuccess(uploadedAssets);
        }

        setExistingAssets((prev) => [...prev, ...uploadedAssets]);
        setFiles([]);
        showNotification("success", `Successfully uploaded  file(s)!`);
      } else {
        throw new Error(response.data.message || "Upload failed");
      }
    } catch (error: any) {
      console.error("Upload failed:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Upload failed. Please try again.";
      setError(errorMessage);
      showNotification("error", errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const showNotification = (type: "success" | "error", message: string) => {
    if (type === "success") {
      Swal.fire({
        icon: "success",
        title: "Success",
        text: message,
        timer: 2000,
        showConfirmButton: false,
        toast: true,
        position: "top-end",
      });
    } else {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: message,
        confirmButtonText: "OK",
        toast: true,
        position: "top-end",
      });
    }
  };

  const handleModalClose = () => {
    files.forEach((file) => {
      URL.revokeObjectURL(file.previewUrl);
    });
    onClose();
  };

  const handleAssetCheckbox = (asset: { url: string; imageTitle: string }) => {
    let updatedAssets;
    const exists = selectedAssets.some((img) => img.url === asset.url);
    if (exists) {
      updatedAssets = selectedAssets.filter((img) => img.url !== asset.url);
    } else {
      updatedAssets = [
        ...selectedAssets,
        { url: asset.url, imageTitle: asset.imageTitle },
      ];
    }
    setSelectedAssets(updatedAssets);
    onSelectionChange(updatedAssets); // Sync with parent
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = e.dataTransfer.files;
    if (!droppedFiles || droppedFiles.length === 0) return;

    // Reuse your handleFileChange logic
    handleFileChange({ target: { files: droppedFiles } } as React.ChangeEvent<HTMLInputElement>);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden relative animate-in slide-in-from-bottom-4 duration-300">
          <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200 bg-neutral-50">
            <div>
              <h2 className="text-lg font-semibold text-black">
                Asset Management
              </h2>
              <p className="mt-1 text-sm text-neutral-600">
                Upload up to {MAX_FILES} high-quality images
              </p>
            </div>
            <button
              onClick={handleModalClose}
              className="p-2 transition-all duration-200 rounded-full hover:bg-neutral-200 
              focus:outline-none focus-visible:border-primary-950"
            >
              <XMarkIcon className="w-6 h-6 text-neutral-500" />
            </button>
          </div>

          <Tab.Group defaultIndex={defaultTab}>
            <Tab.List className="flex gap-0 px-6 border-b border-neutral-200">
              <Tab
                className={({ selected }) =>
                  `flex items-center px-6 py-4 text-sm font-semibold transition-all duration-200 border-b-2 ${selected
                    ? "text-primary-700 border-primary-500 bg-primary-50/50"
                    : "text-neutral-600 border-transparent hover:text-primary-700 hover:border-primary-300"
                  }`
                }
              >
                <ArrowUpTrayIcon className="w-5 h-5 mr-2" />
                Upload New Files
              </Tab>
              <Tab
                className={({ selected }) =>
                  `flex items-center px-6 py-4 text-sm font-semibold transition-all duration-200 border-b-2 ${selected
                    ? "text-primary-700 border-primary-500 bg-primary-50/50"
                    : "text-neutral-600 border-transparent hover:text-primary-700 hover:border-primary-300"
                  }`
                }
              >
                <FolderIcon className="w-5 h-5 mr-2" />
                All Assets ({existingAssets.length + initialSelectedImages.filter(img => !existingAssets.some(a => a.url === img.url)).length})
              </Tab>
            </Tab.List>

            <div className="p-6 max-h-[calc(90vh-160px)] overflow-y-auto">
              <Tab.Panels>
                <Tab.Panel className="space-y-6">
                  <div
                    className={`border-2 border-dashed rounded-xl p-12 text-center transition-all duration-300 ${files.length >= MAX_FILES
                      ? "border-neutral-300 bg-neutral-50 cursor-not-allowed"
                      : isDragging
                        ? "border-primary-400 bg-primary-50/30"
                        : "border-neutral-300 hover:border-primary-400 hover:bg-primary-50/30"
                      }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <input
                      type="file"
                      accept={SUPPORTED_FORMATS.join(",")}
                      multiple
                      onChange={handleFileChange}
                      className="hidden"
                      id="fileUpload"
                      disabled={files.length >= MAX_FILES}
                    />
                    <label
                      htmlFor="fileUpload"
                      className={
                        files.length >= MAX_FILES
                          ? "cursor-not-allowed"
                          : "cursor-pointer"
                      }
                    >
                      <div className="flex flex-col items-center">
                        <ArrowUpTrayIcon
                          className={`w-16 h-16 mb-4 ${files.length >= MAX_FILES
                            ? "text-neutral-300"
                            : "text-primary-400"
                            }`}
                        />
                        <p
                          className={`text-xl font-semibold mb-2 ${files.length >= MAX_FILES
                            ? "text-neutral-400"
                            : "text-black"
                            }`}
                        >
                          {files.length >= MAX_FILES ? (
                            `Maximum ${MAX_FILES} files reached`
                          ) : (
                            <>
                              {isDragging ? (
                                <span className="text-primary-700">Drop files to upload</span>
                              ) : (
                                <>
                                  Drag & drop files here, or{" "}
                                  <span className="text-primary-700 hover:text-primary-700 transition-colors">browse</span>
                                </>
                              )}
                            </>
                          )}
                        </p>
                        <p
                          className={`text-sm ${files.length >= MAX_FILES
                            ? "text-neutral-400"
                            : "text-neutral-500"
                            }`}
                        >
                          {files.length >= MAX_FILES
                            ? "Remove some files to upload more"
                            : `Supports JPG, PNG, GIF • Max 10MB per file • ${MAX_FILES - files.length
                            } remaining`}
                        </p>
                      </div>
                    </label>
                  </div>

                  {error && (
                    <div className="p-4 border border-danger-200 rounded-lg bg-danger-50">
                      <div className="flex">
                        <XMarkIcon className="w-5 h-5 text-danger-400 mt-0.5 flex-shrink-0" />
                        <p className="ml-2 text-sm font-medium text-danger-700">
                          {error}
                        </p>
                      </div>
                    </div>
                  )}

                  {files.length >= MAX_FILES - 1 &&
                    files.length < MAX_FILES && (
                      <div className="p-4 border border-warning-200 rounded-lg bg-warning-50">
                        <div className="flex">
                          <svg
                            className="w-5 h-5 text-warning-400 mt-0.5 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                            />
                          </svg>
                          <p className="ml-2 text-sm font-medium text-warning-700">
                            You can upload {MAX_FILES - files.length} more
                            file(s).
                          </p>
                        </div>
                      </div>
                    )}

                  {files.length > 0 && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium text-black">
                        Files to Upload ({files.length}/{MAX_FILES})
                      </h3>
                      <div className="grid grid-cols-3 gap-6 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                        {files.map((file, idx) => (
                          <div
                            key={idx}
                            className="p-3 bg-gradient-to-br from-neutral-50 to-white rounded-2xl shadow hover:shadow-lg transition-all duration-200 group"
                          >
                            <div className="relative mb-4">
                              <div className="overflow-hidden rounded-xl aspect-square bg-neutral-100 flex items-center justify-center">
                                <img
                                  src={file.previewUrl}
                                  alt={file.name}
                                  className="object-cover w-full h-full transition-transform duration-200 group-hover:scale-105"
                                />
                                <button
                                  onClick={() => removeFile(idx)}
                                  className="absolute top-2 right-2 flex items-center justify-center w-7 h-7 bg-white/70 backdrop-blur-sm text-neutral-700 rounded-full shadow hover:bg-danger-500 hover:text-white transition-colors"
                                  title="Remove"
                                >
                                  <XMarkIcon className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                            <div className="space-y-2">
                              <input
                                type="text"
                                value={file.name}
                                onChange={(e) => {
                                  const updated = [...files];
                                  updated[idx].name = e.target.value;
                                  setFiles(updated);
                                }}
                                className="w-full px-3 py-2 text-sm bg-neutral-50 border border-neutral-200 rounded-lg focus:border-primary-500 transition"
                                placeholder="Image title"
                                required
                              />
                              <select
                                value={file.category}
                                onChange={(e) => {
                                  const updated = [...files];
                                  updated[idx].category = e.target.value;
                                  setFiles(updated);
                                }}
                                className="w-full px-1 py-2 text-sm bg-neutral-50 border border-neutral-200 rounded-lg focus:border-primary-500 transition"
                                required
                              >
                                {IMAGE_CATEGORIES.map((category) => (
                                  <option key={category} value={category}>
                                    {category}
                                  </option>
                                ))}
                              </select>
                              <p className="text-xs text-neutral-400 text-right">{formatFileSize(file.file.size)}</p>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="flex justify-end pt-6 border-t border-neutral-200">
                        <Button
                          onClick={handleUpload}
                          disabled={uploading || files.length === 0}
                          className="duration-200 shadow-md"
                        >
                          {uploading ? (
                            <>
                              <div className="w-4 h-4 mr-2 border-2 border-white rounded-full border-t-transparent animate-spin"></div>
                              Uploading...
                            </>
                          ) : (
                            `Upload ${files.length} File${files.length > 1 ? "s" : ""
                            }`
                          )}
                        </Button>
                      </div>
                    </div>
                  )}
                </Tab.Panel>

                <Tab.Panel>
                  {loadingAssets ? (
                    // Same grid as the asset tiles below.
                    <div
                      className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5"
                      aria-busy="true"
                    >
                      {Array.from({ length: 10 }).map((_, i) => (
                        <Skeleton key={i} className="rounded-lg aspect-square" />
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Always show checkboxes section if showCheckboxes is true */}
                      {showCheckboxes && (
                        <div className="flex items-center gap-3 mb-2">
                          <span className="inline-flex items-center px-3 py-1 text-sm font-semibold text-primary-700 rounded-full bg-primary-50">
                            <ArrowUpTrayIcon className="w-4 h-4 mr-2 text-primary-700" />
                            {selectedAssets.length > 0
                              ? `${selectedAssets.length} image${selectedAssets.length > 1 ? "s" : ""} selected`
                              : "No images selected"}
                          </span>
                          <span className="px-2 py-1 text-xs text-neutral-500 bg-neutral-100 rounded-lg">
                            Selected images will be used for your pitch
                          </span>
                        </div>
                      )}

                      {/* Always show search section */}
                      <div className="flex flex-col gap-3 sm:flex-row">
                        <div className="relative">
                          <select
                            value={searchType}
                            onChange={handleSearchTypeChange}
                            className="px-3 py-2 pr-8 text-sm bg-white border border-neutral-300 appearance-none rounded-l-md focus:outline-none focus:border-primary-500"
                          >
                            <option value="title">File Name</option>
                            <option value="category">Category</option>
                          </select>
                          <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                            <svg className="w-4 h-4 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>

                        <div className="relative flex-1 sm:w-64">
                          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                            <svg className="w-4 h-4 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                          </div>
                          <input
                            type="text"
                            value={searchTerm}
                            onChange={handleSearchChange}
                            placeholder={`Search by ${searchType === "title" ? "File Name" : "category"}...`}
                            className="block w-full py-2 pl-10 pr-10 text-sm placeholder-neutral-500 border border-neutral-300 rounded-r-md focus:outline-none focus:border-primary-500"
                          />
                          {searchTerm && (
                            <button
                              onClick={clearSearch}
                              className="absolute inset-y-0 right-0 flex items-center pr-3"
                            >
                              <XMarkIcon className="w-4 h-4 text-neutral-400 hover:text-primary-700" />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Search Results Info */}
                      {searchTerm && (
                        <div className="p-3 border border-primary-200 rounded-md bg-primary-50">
                          <span className="text-sm text-primary-800">
                            Found {existingAssets.length} result{existingAssets.length !== 1 ? 's' : ''} for "{searchTerm}" in {searchType === "title" ? "File Name" : "categories"}
                          </span>
                          {existingAssets.length > 0 && (
                            <Button variant="link" size="inline" onClick={clearSearch} className="ml-2">
                              Clear search
                            </Button>
                          )}
                        </div>
                      )}

                      {/* Conditional content based on assets and search */}
                      {(() => {
                        // Merge artist library assets with already-attached pitch images so
                        // previously uploaded images always appear in the grid
                        const existingUrls = new Set(existingAssets.map(a => a.url));
                        const pitchOnlyImages = initialSelectedImages
                          .filter(img => !existingUrls.has(img.url))
                          .map(img => ({ ...img, id: 0, userId: 0, artistId: "", category: "", createdAt: "", updatedAt: "" } as Asset));
                        const displayAssets = [...existingAssets, ...pitchOnlyImages];

                        if (displayAssets.length === 0) return searchTerm ? (
                          // No search results found
                          <div className="py-12 text-center">
                            <div className="w-16 h-16 mx-auto mb-4 text-neutral-400">
                              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                              </svg>
                            </div>
                            <h3 className="mb-2 text-lg font-medium text-black">No search results found</h3>
                            <p className="text-neutral-500">
                              No assets found matching "{searchTerm}" in {searchType === "title" ? "File Name" : "categories"}
                            </p>
                            <Button
                              variant="secondary"
                              onClick={clearSearch}
                              className="mt-4"
                            >
                              Clear search to see all assets
                            </Button>
                          </div>
                        ) : (
                          // No assets at all
                          <div className="py-12 text-center">
                            <FolderIcon className="w-16 h-16 mx-auto mb-4 text-neutral-300" />
                            <h3 className="mb-2 text-lg font-medium text-black">
                              No assets yet
                            </h3>
                            <p className="mb-4 text-neutral-500">
                              Upload your first asset to get started.
                            </p>
                          </div>
                        );

                        return (
                        // Show assets grid
                        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                          {displayAssets.map((asset, index) => (
                            <div
                              key={index}
                              className="relative p-4 transition-all duration-200 bg-white border border-neutral-200 shadow-sm rounded-xl hover:shadow-md
                               hover:border-primary-200 group"
                            >
                              {/* Checkbox in top-right */}
                              {showCheckboxes && (
                                <input
                                  type="checkbox"
                                  checked={selectedAssets.some((img) => img.url === asset.url)}
                                  onChange={() => handleAssetCheckbox(asset)}
                                  disabled={
                                    !!(
                                      maxTotalImages &&
                                      selectedAssets.length >= maxTotalImages &&
                                      !selectedAssets.some((img) => img.url === asset.url)
                                    )
                                  }
                                  className="absolute w-5 h-5 top-2 right-2 accent-primary-700"
                                />
                              )}
                              <div
                                className="overflow-hidden bg-neutral-100 rounded-lg cursor-pointer aspect-square"
                                onClick={() => openAssetPreview(asset)}
                              >
                                <img
                                  src={asset.url}
                                  alt={asset.imageTitle}
                                  className="object-cover w-full h-full transition-transform"
                                />
                              </div>
                              <div className="space-y-2">
                                <p className="pt-2 text-sm font-medium text-black truncate">
                                  {asset.imageTitle}
                                </p>
                                {asset.category && (
                                  <p className="text-xs text-neutral-500 truncate">
                                    {asset.category}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        );
                      })()}

                      {/* Save button - only show if checkboxes are enabled */}
                      {showCheckboxes && (
                        <div className="flex justify-end pt-6 border-t border-neutral-200">
                          <Button
                            onClick={handleModalClose}
                            disabled={selectedAssets.length === 0}
                            className="duration-200 shadow-md"
                          >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            {selectedAssets.length === 0 
                              ? "Select images to save" 
                              : `Save ${selectedAssets.length} selected image${selectedAssets.length > 1 ? 's' : ''}`
                            }
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </Tab.Panel>
              </Tab.Panels>
            </div>
          </Tab.Group>
        </div>
      </div>

      {previewModal.isOpen && previewModal.file && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center mt-0 bg-black bg-opacity-90 backdrop-blur-sm"
          onClick={closePreview}
        >
          <div className="relative max-w-7xl max-h-[90vh] w-full mx-4">
            {/* Header */}
            <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between p-4 text-white bg-gradient-to-b from-black/70 to-transparent">
              <h3 className="max-w-md text-lg font-semibold truncate">
                {previewModal.file.name}
              </h3>
              <button
                onClick={closePreview}
                className="p-2 transition-all duration-200 rounded-full hover:bg-white/20 focus:outline-none focus-visible:border-white/50"
              >
                <XMarkIcon className="w-6 h-6 text-white" />
              </button>
            </div>

            {/* Image Container */}
            <div
              className="flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={previewModal.file.previewUrl}
                alt={previewModal.file.name}
                className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
